import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  Radio,
  Text,
  RadioGroup,
  Stack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { EsgLabel, FormInput, NumericFormInput } from "../../../../../../components/Forms";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { showErrorToast, showSuccessToast, translateSubElementString } from "../../../../../../utils";
import {
  supplierQuestionnaireKeys,
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire
} from "../../../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useCreateScopeOne, useUpdateScopeOne } from "../../../../../../hooks/query/supplier-questionnaire/tabs";
import {
  Answer
} from "../../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import { ConfirmationDialog } from "../../../../../../components/ConfirmationDialog";
import { useQueryClient } from "react-query";
import { QuestionIconTooltip } from "../../../../../../components";

const scopeOneAttributes = ["reasonStationaryExclusion", "stationaryEmission", "reasonMobileExclusion", "mobileEmission",
  "reasonFugitiveExclusion", "fugitiveEmission"];

function mapData(inputData) {
  const data = {};
  scopeOneAttributes.forEach(attr => {
    data[attr] = inputData?.[attr] ?? "";
  });
  return data;
}

function ScopeOne({
                    isTabActive, defaultValues, handleTabChange, isPreview,
                    returnToListRequested, declineReturnToListRequest, returnToList
                  }) {
  const { id: questionnaireId, emissionBased: { scopeOne } } = defaultValues;
  const { t } = useTranslation("translation");
  const formDefaultValues = mapData(scopeOne);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const [isDirty, setIsDirty] = useState(false);

  const {
    register,
    formState,
    setValue,
    reset,
    handleSubmit
  } = useForm({
    defaultValues: formDefaultValues
  });

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [direction, setDirection] = useState("");
  const [shouldChangeTab, setShouldChangeTab] = useState(false);
  const [finalizeQuestionnaire, setFinalizeQuestionnaire] = useState(false);

  const [stationaryAccounted, setStationaryAccounted] = useState("yes");
  const [mobileAccounted, setMobileAccounted] = useState("yes");
  const [fugitiveAccounted, setFugitiveAccounted] = useState("yes");

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      if (finalizeQuestionnaire === true) {
        setFinalizeQuestionnaire(false);
      }
      if (returnToListRequested === true) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, finalizeQuestionnaire, returnToListRequested, declineReturnToListRequest, isDirty]);

  useEffect(() => {
    if (isTabActive === true && returnToListRequested === true) {
      const scopeOneForm = document.getElementById("supplier-questionnaire-scope-one");
      if (scopeOneForm) {
        scopeOneForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    }
  }, [isTabActive, returnToListRequested]);

  useEffect(() => {
    if (defaultValues) {
      setStationaryAccounted(defaultValues.emissionBased.scopeOne?.stationaryAccounted === true ? "yes" : "no");
      setMobileAccounted(defaultValues.emissionBased.scopeOne?.mobileAccounted === true ? "yes" : "no");
      setFugitiveAccounted(defaultValues.emissionBased.scopeOne?.fugitiveAccounted === true ? "yes" : "no");
    }
  }, [defaultValues]);

  const changeTab = () => {
    if (shouldChangeTab) {
      if (direction === "previous") {
        handleTabChange(prev => prev - 1);
        setDirection("");
        setShouldChangeTab(false);
      } else if (direction === "next") {
        handleTabChange(prev => prev + 1);
        setDirection("");
        setShouldChangeTab(false);
      }
    }
  };

  const queryConfig = {
    onSuccess: () => {
      if (finalizeQuestionnaire) {
        finalize(questionnaireId);
      } else {
        showSuccessToast(toast, t("entitySaved", { entityName: t("supplierQuestionnaire.scopeOne.entityName") }));
        queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
        if (returnToListRequested === true) {
          returnToList();
        }
        if (!isLoadingOnCreate && !isLoadingOnUpdate) {
          changeTab();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: create, isLoading: isLoadingOnCreate } = useCreateScopeOne(queryConfig);
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateScopeOne(queryConfig);
  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalizeQuestionnaire(false);
    }
  });

  useEffect(() => {
    if (isLoadingOnCreate || isLoadingOnUpdate) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnCreate, isLoadingOnUpdate]);

  const onSubmit = (formData) => {
    const apiData = mapData(formData);
    apiData.stationaryAccounted = stationaryAccounted === "yes";
    apiData.mobileAccounted = mobileAccounted === "yes";
    apiData.fugitiveAccounted = fugitiveAccounted === "yes";

    if (isDirty || apiData.stationaryAccounted !== scopeOne?.stationaryAccounted ||
      apiData.mobileAccounted !== scopeOne?.mobileAccounted || apiData.fugitiveAccounted !== scopeOne?.fugitiveAccounted) {
      if (scopeOne?.id) {
        update({ questionnaireId, id: scopeOne.id, data: apiData });
      } else {
        create({ questionnaireId, data: apiData });
      }
      reset(formData);
    } else {
      return finalizeQuestionnaire ? finalize(questionnaireId) :
        returnToListRequested === true ? returnToList() :
          shouldChangeTab ? changeTab() : null;
    }
  };

  const handleFinalizeConfirmation = () => {
    handleSubmit(onSubmit)();
    handleDialogClose(true);
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalizeQuestionnaire(false);
    }
    onConfirmDialogClose();
  };

  return (
    <chakra.form id="supplier-questionnaire-scope-one" onSubmit={handleSubmit(onSubmit)}>

      <Flex direction="column">

        <Box>
          {isPreview ?
            <Flex alignItems="center" whiteSpace="break-spaces">
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.stationaryTooltip")}
                ariaLabel="stationary-tooltip"
              />
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.stationary")}
              </Heading>
              {` - `}
              <Answer
                answerText={stationaryAccounted ? t(`supplierQuestionnaire.scopeOne.buttons.${stationaryAccounted}`) : ""}
              />
            </Flex> :
            <Flex alignItems="center">
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.stationary")}
              </Heading>
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.stationaryTooltip")}
                ariaLabel="stationary-tooltip"
              />
            </Flex>
          }

          <Flex gap={20} alignItems="center">
            {isPreview ?
              null :
              <RadioGroup value={stationaryAccounted} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="yes"
                    onChange={e => {
                      setStationaryAccounted(e?.target.value);
                      setValue("reasonStationaryExclusion", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="no"
                    onChange={e => {
                      setStationaryAccounted(e?.target.value);
                      setValue("stationaryEmission", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
            }

            {stationaryAccounted === "yes" ?
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")} />
                  <Answer answerText={formDefaultValues.stationaryEmission?.toString()} />
                </Box> :
                <NumericFormInput
                  label={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")}
                  errors={formState.errors}
                  {...register("stationaryEmission")}
                  disabled={stationaryAccounted === "no"}
                />
              :
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.reasonForExclusion")} />
                  <Answer answerText={formDefaultValues.reasonStationaryExclusion} />
                </Box> :
                <FormInput
                  label={t("supplierQuestionnaire.scopeOne.reasonForExclusion")}
                  errors={formState.errors}
                  {...register("reasonStationaryExclusion")}
                  disabled={stationaryAccounted === "yes"}
                />
            }
          </Flex>
        </Box>


        <Box marginBlockStart={10}>
          {isPreview ?
            <Flex alignItems="center" whiteSpace="break-spaces">
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.mobileTooltip")}
                ariaLabel="mobile-tooltip"
              />
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.mobile")}
              </Heading>
              {` - `}
              <Answer
                answerText={mobileAccounted ? t(`supplierQuestionnaire.scopeOne.buttons.${mobileAccounted}`) : ""}
              />
            </Flex> :
            <Flex alignItems="center">
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.mobile")}
              </Heading>
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.mobileTooltip")}
                ariaLabel="mobile-tooltip"
              />
            </Flex>
          }

          <Flex gap={20} alignItems="center">
            {isPreview ? null :
              <RadioGroup value={mobileAccounted} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="yes"
                    onChange={e => {
                      setMobileAccounted(e?.target.value);
                      setValue("reasonMobileExclusion", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="no"
                    onChange={e => {
                      setMobileAccounted(e?.target.value);
                      setValue("mobileEmission", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
            }

            {mobileAccounted === "yes" ?
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")} />
                  <Answer answerText={formDefaultValues.mobileEmission?.toString()} />
                </Box> :
                <NumericFormInput
                  label={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")}
                  errors={formState.errors}
                  {...register("mobileEmission")}
                  disabled={mobileAccounted === "no"}
                />
              :
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.reasonForExclusion")} />
                  <Answer answerText={formDefaultValues.reasonMobileExclusion} />
                </Box> :
                <FormInput
                  label={t("supplierQuestionnaire.scopeOne.reasonForExclusion")}
                  errors={formState.errors}
                  {...register("reasonMobileExclusion")}
                  disabled={mobileAccounted === "yes"}
                />
            }
          </Flex>
        </Box>

        <Box marginBlockStart={10}>
          {isPreview ?
            <Flex alignItems="center" whiteSpace="break-spaces">
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.fugitiveTooltip")}
                ariaLabel="fugitive-tooltip"
              />
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.fugitive")}
              </Heading>
              {` - `}
              <Answer
                answerText={fugitiveAccounted ? t(`supplierQuestionnaire.scopeOne.buttons.${fugitiveAccounted}`) : ""}
              />
            </Flex> :
            <Flex alignItems="center">
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeOne.fugitive")}
              </Heading>
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeOne.fugitiveTooltip")}
                ariaLabel="fugitive-tooltip"
              />
            </Flex>
          }

          <Flex gap={20} alignItems="center">
            {isPreview ? null :
              <RadioGroup value={fugitiveAccounted} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="yes"
                    onChange={e => {
                      setFugitiveAccounted(e?.target.value);
                      setValue("reasonFugitiveExclusion", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="no"
                    onChange={e => {
                      setFugitiveAccounted(e?.target.value);
                      setValue("fugitiveEmission", "", { shouldDirty: true });
                    }}
                  >
                    {t("supplierQuestionnaire.scopeOne.buttons.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
            }

            {fugitiveAccounted === "yes" ?
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")} />
                  <Answer answerText={formDefaultValues.fugitiveEmission?.toString()} />
                </Box> :
                <NumericFormInput
                  label={translateSubElementString("supplierQuestionnaire.scopeOne.tCO2e")}
                  errors={formState.errors}
                  {...register("fugitiveEmission")}
                  disabled={fugitiveAccounted === "no"}
                />
              :
              isPreview ?
                <Box>
                  <EsgLabel text={translateSubElementString("supplierQuestionnaire.scopeOne.reasonForExclusion")} />
                  <Answer answerText={formDefaultValues.reasonFugitiveExclusion} />
                </Box> :
                <FormInput
                  label={t("supplierQuestionnaire.scopeOne.reasonForExclusion")}
                  errors={formState.errors}
                  {...register("reasonFugitiveExclusion")}
                  disabled={fugitiveAccounted === "yes"}
                />
            }
          </Flex>
        </Box>
      </Flex>

      {!isPreview ?
        <Flex
          justifyContent="space-between"
          marginBlockStart="10"
          position="sticky"
          bottom="0"
          backgroundColor="#fff"
          borderColor="esg_gray.100"
          bordersyle="solid"
          borderTopWidth="1px"
          paddingBlock="4"
          zIndex="10"
        >
          <Flex gap="2">
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              width="32"
              type="submit"
              onClick={() => {
                setDirection("previous");
                setShouldChangeTab(true);
              }}
            >
              {t("buttons.previous")}
            </Button>
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              width="32"
              type="submit"
              onClick={() => {
                setDirection("next");
                setShouldChangeTab(true);
              }}
            >
              {t("buttons.next")}
            </Button>
          </Flex>
          <Flex gap="2">
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              type="submit"
              width="32"
              onClick={() => setShouldChangeTab(false)}
            >
              {t("buttons.save")}
            </Button>
            <Button
              colorScheme="customScheme"
              width="32"
              onClick={() => {
                setFinalizeQuestionnaire(true);
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Flex>
        </Flex> : null}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </chakra.form>
  );
}

export default ScopeOne;
